// src/pages/HspGroupMembershipsPage.tsx
//
// ★ PHASE 6, TASK 21 — Annex A story 30: "HSP Information Tagged to HSP Group".
//
// The membership half of stories 29/30 — which HSP (an `app.agency` of kind
// HSP) is tagged to which group (`HspGroupsPage`). Deliberately does NOT
// carry HSP identity or contact information — see
// `036_hsp_group_membership.sql`'s own header. "Reassign" is retire the old
// pairing, then establish a new one; there is no single "move" action on the
// API, so this screen offers both as one form with a group picker.
import { useEffect, useState, type FormEvent } from 'react'
import { SgdsButton, SgdsInput } from '@govtechsg/sgds-web-component/react'
import { api, ApiError} from '../lib/api'
import { compositeSubject, splitCompositeSubject } from '../lib/composite-subject'
import { useApi } from '../lib/useApi'
import { useOffsetPager } from '../lib/useOffsetPager'
import { Pagination } from '../components/Pagination'
import { useSort, sortParams } from '../lib/useSort'
import { SortableTh, SortAnnouncement } from '../components/SortableTh'
import { EmptyCard, ErrorCard, LoadingCard } from '../components/StateCards'
import { VersionHistory } from '../components/VersionHistory'
import { FormResult } from '../components/FormResult'
import { BoundedField } from '../components/BoundedField'
import { DatePicker } from '../components/DatePicker'
import { ConfirmWrite } from '../components/ConfirmWrite'
import { boundsProblems } from '../lib/published-bounds'
import { targetValue } from '../lib/sgds-dom'
import { formatIsoDate, formatInstant } from '../lib/format'
import { EmailReportButton } from '../components/EmailReportButton'
import { ExportToolbar } from '../components/ExportToolbar'

type HspGroupMembershipSortKey = 'hspAgencyCode' | 'hspAgencyName' | 'groupCode' | 'serviceOfferings' | 'effectiveFrom'

const COLUMN_LABELS: Readonly<Record<HspGroupMembershipSortKey, string>> = {
  hspAgencyCode: 'HSP',
  hspAgencyName: 'HSP name',
  groupCode: 'Group',
  serviceOfferings: 'Service offerings',
  effectiveFrom: 'Effective from',
}

/**
 * ★★ WAVE 8 (Task 3) — ONE copy of each bounded field's label: read by
 * `boundsProblems` for its messages AND by the field that renders it. Two copies
 * drift the day one of them is reworded.
 *
 * ⚠ Only fields the SERVER publishes a bound for appear here. A field the route
 * checks for presence alone is deliberately absent, and its screen therefore says
 * nothing beyond presence — see `routes/administration-field-bounds.ts`.
 */
const BOUNDED_FIELDS = {
  groupCode: 'Group',
  note: 'Note (optional)',
} as const

type FormState =
  | { status: 'idle' }
  | { status: 'submitting' }
  | { status: 'error'; message: string }
  | { status: 'success'; message: string }

/**
 * Rows per page. Comfortably under this surface's own `MAX_*_LIMIT` at the
 * gateway, so the server never has to clamp what the screen asked for — a
 * clamped request would silently show fewer rows than the pager's arithmetic
 * assumes and put the page count out by one.
 */
const PAGE_SIZE = 25

export function HspGroupMembershipsPage() {
  // ★★★ WAVE 12 (P4) — the version history, for a COMPOSITE-KEY catalogue.
  //
  // ⚠ This series needs BOTH keys. Called with only the first, the route answers
  // with the agency's CURRENT BOARD instead of one pair's history — a different
  // question, and one that would render plausibly under a "Version history"
  // heading. So the picker offers a compound subject and `splitCompositeSubject`
  // reads it back; a subject that is not a pair fetches NOTHING rather than
  // guessing.
  const [historySubject, setHistorySubject] = useState('')
  const [historyAsRecordedAt, setHistoryAsRecordedAt] = useState('')
  const history = useApi(() => {
    const pair = splitCompositeSubject(historySubject)
    if (pair === undefined) return Promise.resolve(null)
    return api.hspGroupMembershipSeries(pair.first, pair.second, historyAsRecordedAt)
  }, [historySubject, historyAsRecordedAt])
  const { sort, onSort, interacted } = useSort<HspGroupMembershipSortKey>()
/**
   * ★★ THE PAGE OFFSET — 2026-08-18. This screen showed the first page of a
   * population it also told the officer the size of, and no control could reach
   * the rest; `services/gateway/src/list-reachability.test.ts` is the census
   * that found it. Held here rather than inside `useOffsetPager` because the
   * FETCH is keyed on it, and the hook is deliberately unaware of fetching.
   */
  const [pagerOffset, setPagerOffset] = useState(0)

  const memberships = useApi(
    () => api.hspGroupMemberships({ ...sortParams(sort), offset: pagerOffset }),
    [sort.column, sort.dir, pagerOffset],
  )

  const [hspAgencyCode, setHspAgencyCode] = useState('')
  const [groupCode, setGroupCode] = useState('')
  const [serviceOfferings, setServiceOfferings] = useState('')
  const [effectiveFrom, setEffectiveFrom] = useState('')
  const [note, setNote] = useState('')
  const [retireInstead, setRetireInstead] = useState(false)
  const [form, setForm] = useState<FormState>({ status: 'idle' })

  const rows = memberships.data?.memberships ?? []
  const total = memberships.data?.total ?? 0
  const pager = useOffsetPager(PAGE_SIZE, total)

  // ⚠ The pager owns the page NUMBER; this component owns the offset it FETCHES
  // with. Kept in step in one place rather than becoming two sources of one
  // fact — S-011's shape, in a component.
  useEffect(() => {
    if (pager.offset !== pagerOffset) setPagerOffset(pager.offset)
  }, [pager.offset, pagerOffset])

  // ⚠ A FILTER or SORT change is a different population. Page three of one is
  // not page three of the other, and leaving the offset alone shows an empty
  // table that reads as "no results" when there are plenty.
  useEffect(() => {
    pager.reset()
  }, [sort.column, sort.dir])

  /*
    ⚠ The bounds come from the PAYLOAD (`validation`), never from this file, and
    they are checked against the value that will actually be SENT rather than the
    keystrokes in the box — see `lib/published-bounds.ts` for why both of those
    matter. `undefined` while the list is loading or refused, in which case the
    fields show nothing beyond `required` and the route refuses on its own.
  */
  const validation = memberships.data?.validation
  const problems = boundsProblems(
    validation,
    {
      groupCode: groupCode.trim().toUpperCase(),
      note: note.trim(),
    },
    BOUNDED_FIELDS,
  )

  const canSubmit =
    hspAgencyCode.trim().length > 0 && groupCode.trim().length > 0 && effectiveFrom.length > 0 && !problems.any

  /**
   * ★★ WAVE 26 — Save FREEZES a sentence and asks; the write happens only from the
   * confirmation (`ConfirmWrite`'s header says why every irreversible write does
   * this now). Composed once, from the fields as they were when Save was pressed —
   * never re-derived from a form the officer may since have edited.
   */
  const [confirming, setConfirming] = useState<string | null>(null)

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (!canSubmit) return
    const hsp = hspAgencyCode.trim().toUpperCase()
    const group = groupCode.trim().toUpperCase()
    const offerings = serviceOfferings.trim()
    setConfirming(
      retireInstead
        ? `Retire the tag of HSP ${hsp} to group ${group} from ${effectiveFrom}. From that day the HSP is no longer a member of that group. There is no undo. A mistake is corrected by another version.`
        : `Tag HSP ${hsp} to group ${group} from ${effectiveFrom}${offerings.length > 0 ? `: service offerings ${offerings}` : ''}. Any open tag for this pair closes on that day and this one takes its place. There is no undo. A mistake is corrected by another version.`,
    )
  }

  const performWrite = async () => {
    setConfirming(null)
    setForm({ status: 'submitting' })
    try {
      const outcome = (await api.supersedeHspGroupMembership(hspAgencyCode.trim().toUpperCase(), {
        groupCode: groupCode.trim().toUpperCase(),
        effectiveFrom,
        establish: !retireInstead,
        ...(note.trim().length > 0 && { note: note.trim() }),
        ...(!retireInstead && { serviceOfferings: serviceOfferings.trim().length > 0 ? serviceOfferings.trim() : null }),
      })) as { outcome: string }
      setForm({
        status: 'success',
        message:
          outcome.outcome === 'RETIRED'
            ? `${hspAgencyCode.trim().toUpperCase()} -> ${groupCode.trim().toUpperCase()} retired from ${effectiveFrom}.`
            : `${hspAgencyCode.trim().toUpperCase()} -> ${groupCode.trim().toUpperCase()} established from ${effectiveFrom}.`,
      })
      setHspAgencyCode('')
      setGroupCode('')
      setServiceOfferings('')
      setEffectiveFrom('')
      setNote('')
      memberships.reload()
    } catch (error) {
      setForm({ status: 'error', message: error instanceof ApiError ? error.message : 'Could not save this membership.' })
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4 flex-wrap pt-no-print">
        <div>
          <h1 className="text-3xl mb-2">HSP group membership</h1>
          <p className="text-[var(--sgds-body-color-subtle)] max-w-[70ch]">
            Which HSP is tagged to which group. HSP identity and contact information live
            elsewhere (the agency register and the point-of-contact directory): this catalogue is the TAG only.
            An HSP may hold open membership in more than one group at once; "reassigning" means retiring the old
            tag and establishing a new one. Effective-dated, PSD-only.
          </p>
        </div>
        <div className="flex gap-2">
          {/*
            ★★★ WAVE 27 — one control group, owner-directed. These answer a single
            question — "what do I want to do with this list?" — and were arranged as if
            they answered three or four. See `ExportToolbar` for the whole argument.
          */}
          <ExportToolbar
            onDownload={(format) => {
              window.location.href = api.hspGroupMembershipExportUrl(format)
            }}
            email={<EmailReportButton path="/api/administration/hsp-group-memberships/export" />}
          />
        </div>
      </div>

      <SortAnnouncement sort={sort} interacted={interacted} busy={memberships.loading} columnLabels={COLUMN_LABELS} rowCount={rows.length} />

      {memberships.error !== null ? (
        <ErrorCard title="Could not load HSP group membership" message={memberships.error} onRetry={memberships.reload} />
      ) : memberships.initialLoading ? (
        <LoadingCard label="Loading HSP group membership" rows={4} />
      ) : rows.length === 0 ? (
        <EmptyCard
          title="No memberships in scope"
          message="Either nothing is currently in force, or your active identity is not PSD: this catalogue is administered by PSD only."
        />
      ) : (
        <div className="pt-card p-0 overflow-hidden">
          <div className="pt-table-wrap" aria-busy={memberships.loading}>
            <table className="pt-table">
              <caption className="sr-only">HSP group memberships in force, with the provider, its name, the group, service offerings and effective date</caption>
              <thead>
                <tr>
                  <SortableTh label="HSP" sortKey="hspAgencyCode" sort={sort} onSort={onSort} />
                  <SortableTh label="HSP name" sortKey="hspAgencyName" sort={sort} onSort={onSort} />
                  <SortableTh label="Group" sortKey="groupCode" sort={sort} onSort={onSort} />
                  <SortableTh label="Service offerings" sortKey="serviceOfferings" sort={sort} onSort={onSort} />
                  <SortableTh label="Effective from" sortKey="effectiveFrom" sort={sort} onSort={onSort} />
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td className="font-semibold pt-mono">{row.hspAgencyCode}</td>
                    <td>{row.hspAgencyName}</td>
                    <td className="pt-mono">{row.groupCode}</td>
                    <td className="text-sm">{row.serviceOfferings ?? 'None'}</td>
                    <td className="text-sm" title={formatInstant(row.recordedAt)}>
                      {formatIsoDate(row.effective.from)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-sm text-[var(--sgds-color-subtle)] px-5 py-3 border-t border-[var(--sgds-border-color-muted)] pt-no-print">
            Showing {rows.length} of {total} currently in-force membership{total === 1 ? '' : 's'}.{' '}
            {sort.column === null ? 'The server default order.' : `Sorted by ${COLUMN_LABELS[sort.column]} (${sort.dir}).`}
          </p>
          <Pagination pager={pager} label="memberships" total={total} shown={rows.length} />
        </div>
      )}

      {/* ★★★ WAVE 12 (P4) — the version history. Compound subject, because this
          catalogue's series is keyed by a pair; see the hooks above. */}
      <VersionHistory
        subject={historySubject}
        subjectOptions={rows.map((row) => compositeSubject(row.hspAgencyCode, row.groupCode))}
        subjectHint="The provider / group pairs listed above. Sort or filter that table to reach others."
        onSubjectChange={(next) => {
          setHistoryAsRecordedAt('')
          setHistorySubject(next)
        }}
        entries={history.data?.entries ?? []}
        loading={history.loading}
        error={history.error}
        onRetry={history.reload}
        asRecordedAt={historyAsRecordedAt}
        onAsRecordedAtChange={setHistoryAsRecordedAt}
        columns={[
            { header: 'Provider', cell: (entry) => entry.hspAgencyCode },
            { header: 'Group', cell: (entry) => entry.groupCode },
        ]}
      />
      <div className="pt-card flex flex-col gap-5 pt-no-print">
        <div>
          <h2 className="text-xl mb-1">Tag or retire a membership</h2>
          <p className="text-sm text-[var(--sgds-body-color-subtle)]">
            The HSP must be registered as an agency of kind HSP: checked against the registered agencies before
            anything is written.
          </p>
        </div>
        <form className="grid gap-4 sm:grid-cols-2" onSubmit={handleSubmit}>
          <SgdsInput placeholder="" label="HSP" hintText="e.g. SGH, NUH" value={hspAgencyCode} required onSgdsInput={(e) => setHspAgencyCode(targetValue(e))} />
          <BoundedField
            label={BOUNDED_FIELDS.groupCode}
            example="e.g. ACUTE_HOSP."
            value={groupCode}
            onValueChange={setGroupCode}
            bounds={validation?.groupCode}
            problem={problems.of.groupCode}
            required
          />
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-semibold">
              Effective from <span aria-hidden="true">*</span>
              <span className="sr-only">required</span>
            </span>
            <DatePicker value={effectiveFrom} onChange={setEffectiveFrom} required />
          </label>
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input type="checkbox" checked={retireInstead} onChange={(e) => setRetireInstead(e.target.checked)} />
            Retire this tag instead of establishing it
          </label>
          {!retireInstead && (
            <div className="sm:col-span-2">
              <SgdsInput placeholder=""
                label="Service offerings (optional)"
                value={serviceOfferings}
                onSgdsInput={(e) => setServiceOfferings(targetValue(e))}
              />
            </div>
          )}
          <BoundedField
            className="sm:col-span-2"
            label={BOUNDED_FIELDS.note}
            value={note}
            onValueChange={setNote}
            bounds={validation?.note}
            problem={problems.of.note}
            multiline
          />
          {confirming !== null && (
            <ConfirmWrite
              summary={confirming}
              confirmLabel="Yes, apply it"
              onConfirm={() => void performWrite()}
              onCancel={() => setConfirming(null)}
            />
          )}
          <FormResult state={form} className="sm:col-span-2" />
          <div className="sm:col-span-2">
            <SgdsButton type="submit" variant="primary" disabled={!canSubmit || confirming !== null} loading={form.status === 'submitting'}>
              {retireInstead ? 'Retire' : 'Save'}
            </SgdsButton>
          </div>
        </form>
      </div>
    </div>
  )
}
